<template>
  <div>
    <div class="row">
      <div class="col-6">
        <h3>Select options</h3>
      </div>
    </div>

    <!--Type -->
    <div class="row mt-2">
      <div class="col">
        <b-button-group size="sm">
          <b-button
            v-for="type in types"
            :key="type"
            @click="selectedType = type"
            :variant="selectedType && selectedType === type ? 'info' : ''"
            >{{ type }}</b-button
          >
        </b-button-group>
      </div>
    </div>

    <!--Tumour -->
    <div class="row mt-2" v-if="selectedType">
      <div class="col">
        <b-button-group size="sm">
          <b-button
            v-for="tumour in tumours"
            :key="tumour"
            @click="selectedTumour = tumour"
            :variant="selectedTumour && selectedTumour === tumour ? 'info' : ''"
            >{{ tumour }}</b-button
          >
        </b-button-group>
      </div>
    </div>

    <!--Nodes -->
    <div class="row mt-2" v-if="selectedTumour">
      <div class="col">
        <b-button-group size="sm">
          <b-button
            v-for="node in nodes"
            :key="node"
            @click="selectedNode = node"
            :variant="selectedNode && selectedNode === node ? 'info' : ''"
            >{{ node }}</b-button
          >
        </b-button-group>
      </div>
    </div>

    <!--Metastases -->
    <div class="row mt-2" v-if="selectedNode">
      <div class="col">
        <b-button-group size="sm">
          <b-button
            v-for="metastasesInfo in metastases"
            :key="metastasesInfo"
            @click="selectedMetastases = metastasesInfo"
            :variant="
              selectedMetastases && selectedMetastases === metastasesInfo
                ? 'info'
                : ''
            "
            >{{ metastasesInfo }}</b-button
          >
        </b-button-group>
      </div>
    </div>

    <!--Extra questions -->
    <div
      v-for="(questions, index) in extraQuestions"
      :key="index"
      class="mt-2 mb-3"
    >
      <b-form-group >
       <b-form-radio-group
          :options="questions.entries"
          :id="'radio-group-'+ index"
          value-field="id"
          text-field="name"
          :name="'radio-group-'+ index"
          @change="selectResult(questions.id, $event, index)"
        >
        </b-form-radio-group>
      </b-form-group>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ResultInfo, TnmModelTest } from "@/entities/TnmModel";
import { HttpHandler } from "@/http/HttpHandler";
import store from "@/store/index";

@Component
export default class TnmStageSelector extends Vue {
  @Prop()
  branchId: number | null = null;
  private localBranchId: number | null = this.branchId;

  @Prop()
  hideSafetySelector: boolean = false;
  private localHideSafetySelector: boolean = this.hideSafetySelector;

  @Prop()
  hideTreatmentResultCheckbox: boolean = false;
  private localHideTreatmentResultCheckbox: boolean = this.hideTreatmentResultCheckbox;

  private selectedType: string = "";
  private selectedTumour: string | null = null;
  private selectedNode: string | null = null;
  private selectedMetastases: string | null = null;
  private tnmModel: TnmModelTest[] | null = [];

  tumours: string[] = [];
  nodes: string[] = [];
  metastases: string[] = [];
  extraQuestions: {
    id: string;
    parentId: string;
    entries: ResultInfo[];
  }[] = [];

  types = ["NSCLC", "SCLC"];

  @Watch("localBranchId")
  onLocalBranchIdChanged() {
    this.$emit("update:branchId", this.localBranchId);
  }

  @Watch("branchId")
  onBranchIdChanged() {
    this.localBranchId = this.branchId;
  }

  @Watch("localHideSafetySelector")
  onLocalHideSafetySelectorChanged() {
    this.$emit("update:hideSafetySelector", this.localHideSafetySelector);
  }

  @Watch("localHideTreatmentResultCheckbox")
  onLocalHideTreatmentResultCheckbox() {
    this.$emit("update:hideTreatmentResultCheckbox", this.localHideTreatmentResultCheckbox);
  }

  @Watch("selectedType")
  async onTypeChanged(type: string) {
    this.selectedType = type;

    this.tumours =
      this.tnmModel
        ?.map(m => m.tumour)
        .filter(TnmStageSelector._onlyUnique)
        .sort() || [];

    this.nodes = [];
    this.metastases = [];
    this.selectedTumour = null;
    this.selectedNode = null;
    this.selectedMetastases = null;
    this.extraQuestions = [];
  }

  @Watch("selectedTumour")
  onTumourChanged(tumour: string) {
    this.selectedTumour = tumour;

    this.nodes =
      this.tnmModel
        ?.filter(m => m.tumour === tumour)
        .map(m => m.node)
        .filter(TnmStageSelector._onlyUnique)
        .sort() || [];
    this.selectedNode = null;
    this.selectedMetastases = null;
    this.hideSafetySelector = false;
    this.extraQuestions = [];
  }

  @Watch("selectedNode")
  onNodeChanged(node: string) {
    this.localBranchId = null;
    this.metastases =
      this.tnmModel
        ?.filter(m => m.tumour == this.selectedTumour && m.node === node)
        .map(m => m.metastases)
        .filter(TnmStageSelector._onlyUnique)
        .sort() || [];
    this.selectedMetastases = null;
    this.extraQuestions = [];
  }

  @Watch("selectedMetastases")
  onMetastasesChanged(metastases: string) {
    this.localBranchId = null;
    this.extraQuestions = [];

    if (this.metastases) {
      let result = this.tnmModel?.filter(
        m =>
          m.tumour == this.selectedTumour &&
          m.node === this.selectedNode &&
          m.metastases === metastases
      )[0];

      if (!result) {
        return;
      }

      const resultInfo =
        this.selectedType === "NSCLC"
          ? result.resultInfoNsclc
          : result.resultInfoSclc;
      if (resultInfo.branchId) {
        this.localBranchId = resultInfo.branchId;
        this.localHideSafetySelector = resultInfo.hideSafetySelector;
        this.localHideTreatmentResultCheckbox = resultInfo.hideTreatmentResultCheckbox;
      } else if (resultInfo.childList.length) {
        let result = this.extraQuestions.find(q => q.id === resultInfo.id);
        if (!result) {
          this.extraQuestions.push({
            id: `${this.selectedTumour}${this.selectedMetastases}${this.metastases}`,
            parentId: resultInfo.id,
            entries: resultInfo.childList
          });
        } else {
          result.entries = resultInfo.childList;
        }
      }
    }
  }

  selectResult(parentId: any, itemId: string, index: number) {
    console.log("parentId", parentId, "itemId", itemId, index);

    this.extraQuestions = this.extraQuestions.filter(
      q => q.parentId !== "" && q.parentId !== parentId
    );

    let parent = this.extraQuestions.find(q => q.id === parentId);
    if (parent) {
      let child = parent.entries.find(q => q.id === itemId);
      console.log("selectResult child", child);
      if (!child) {
        return;
      }
      if (child.branchId) {
        this.localBranchId = child.branchId;
        this.localHideSafetySelector = child.hideSafetySelector;
      } else if (child.id && child.childList.length) {
        this.localBranchId = null;
        let result = this.extraQuestions.find(q => q.id === itemId);
        if (!result) {
          this.extraQuestions.push({
            id: child.id,
            parentId: parentId,
            entries: child.childList
          });
        } else {
          result.entries = child.childList;
        }
      }
    }
  }

  async created() {
    this.localBranchId = null;
    this.tnmModel = await this.getData();
  }

  async getData() {
    let httpHandler = new HttpHandler(store);
    return await httpHandler.getToolMatrix(
      this.selectedTumour,
      this.selectedNode,
      this.selectedMetastases
    );
  }

  private static _onlyUnique(value: string, index: number, self: string[]) {
    return self.indexOf(value) === index;
  }
}
</script>

<style scoped lang="scss">
@import "node_modules/bootstrap/scss/_functions.scss";
@import "node_modules/bootstrap/scss/_variables.scss";
@import "node_modules/bootstrap/scss/_mixins.scss";

.scrolled {
  overflow-y: scroll;
  height: calc(100vh - 180px);
  overflow-x: hidden;

  @include media-breakpoint-down(xs) {
    height: calc(100vh - 80px);
  }
}

.link {
  color: #007bff;
  cursor: pointer;
}

.disable-link {
  color: grey;
  cursor: unset;
}
</style>
